import type { IReportData, IReportEntry } from "./Report.types";

/**
 * Fills an entry's idata/secdata from the report's signature image map,
 * recursing into its history items.
 */
const resolveEntry = (
  entry: IReportEntry,
  images: Record<string, string>,
): IReportEntry => ({
  ...entry,
  idata: entry.idata ?? (entry.idataKey ? images[entry.idataKey] : entry.idata),
  secdata:
    entry.secdata ??
    (entry.secdataKey ? images[entry.secdataKey] : entry.secdata),
  history: entry.history?.map(h => resolveEntry(h, images)),
});

/**
 * Resolves the deduplicated signature images a report response may send in
 * place of per-entry blobs, so entries carry their idata/secdata as before.
 *
 * @param data - The report payload, in either the old or the new shape.
 * @returns The payload with signatures resolved, or unchanged when the
 * response has no signatureImages map.
 */
export const resolveSignatures = <T extends Partial<IReportData>>(
  data: T,
): T => {
  const images = data?.signatureImages;
  if (!images || !Array.isArray(data.result)) return data;
  return {
    ...data,
    result: data.result.map(entry => resolveEntry(entry, images)),
  };
};
